import { FormHelperText, Grid, IconButton, InputAdornment, MenuItem, Typography } from "@mui/material"
import {
    FormikChip,
    FormikDropdown,
    FormikSwitch,
    FormikTextField,
    FormikTimePicker,
} from "../../../../../shared/components/forms/Formik"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { SelectProductGroupCode } from "../../productGroup/components/SelectProductGroupCode"
import { FormikPriceListField } from "../../products/components/FormikPriceListsField"
import {
    CreateUpdateExtraServiceFormValues,
    ExtraServiceType,
    ExtraServicePricingType,
    ExtraServiceActionType,
} from "../extraServicesTypes"
import { iterateStringEnum } from "../../../../../lib/enums"
import { DaysOfWeek } from "../../../merchant/menuPlanning/menuPlanningTypes"
import { PriceWithCurrency } from "./PriceWithCurrency"
import { useField, useFormikContext, ErrorMessage } from "formik"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose } from "@fortawesome/pro-light-svg-icons"

type CreateUpdateExtraServicesFormContentProps = Readonly<{}>

export function CreateUpdateExtraServiceFormContent({}: CreateUpdateExtraServicesFormContentProps) {
    const [field] = useField("pricingType")
    const { setFieldValue } = useFormikContext()
    const [{ value: action }] = useField("action")

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <FormikPriceListField<CreateUpdateExtraServiceFormValues> name="priceList" />
                    <LocalizedStrict id="extra-services-create-service-type-field" attrs={{ label: true }}>
                        <FormikDropdown sx={{ width: "40%" }} name="serviceType" label="Service type">
                            {[...iterateStringEnum(ExtraServiceType)].map((type) => (
                                <MenuItem key={type} value={type}>
                                    <LocalizedEnum
                                        base="extra-services-create-form-type-dropdown-item"
                                        enumValue={type}
                                    >
                                        <span>{type}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="extra-services-create-service-name-field" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Name" fullWidth />
                    </LocalizedStrict>
                    <LocalizedStrict id="extra-services-create-service-pricing-type-field" attrs={{ label: true }}>
                        <FormikDropdown fullWidth name="pricingType" label="Pricing type">
                            {[...iterateStringEnum(ExtraServicePricingType)].map((type) => (
                                <MenuItem key={type} value={type}>
                                    <LocalizedEnum
                                        base="extra-services-create-form-pricing-type-dropdown-item"
                                        enumValue={type}
                                    >
                                        <span>{type}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>

                    {field.value === ExtraServicePricingType.PER_SEAT ? (
                        <>
                            <PriceWithCurrency
                                priceName="pricePerSeat"
                                priceLabelId="extra-services-create-price-per-seat-field-label"
                                currencyName="pricePerSeatCurrency"
                                currencyLabelId="product-create-currency-field-label"
                                priceFallbackLabel="Price per seat"
                            />
                            <PriceWithCurrency
                                priceName="minimumPrice"
                                priceLabelId="extra-services-create-min-price-field-label"
                                currencyName="minimumPriceCurrency"
                                priceFallbackLabel="Min.price"
                                currencyLabelId="product-create-currency-field-label"
                            />
                            <PriceWithCurrency
                                priceName="maximumPrice"
                                priceLabelId="extra-services-create-max-price-field-label"
                                currencyName="maximumPriceCurrency"
                                priceFallbackLabel="Max.price"
                                currencyLabelId="product-create-currency-field-label"
                            />
                        </>
                    ) : field.value === ExtraServicePricingType.FLAT_FEE ? (
                        <PriceWithCurrency
                            priceName="defaultPrice"
                            priceLabelId="extra-services-create-price-field-label"
                            currencyName="defaultCurrency"
                            priceFallbackLabel="Price"
                            currencyLabelId="product-create-currency-field-label"
                        />
                    ) : null}
                    <LocalizedStrict id="extra-services-create-service-description-field" attrs={{ label: true }}>
                        <FormikTextField
                            inputProps={{
                                maxLength: 500,
                            }}
                            name="description"
                            label="Description"
                            multiline
                            minRows={3}
                            maxRows={4}
                            fullWidth
                        />
                    </LocalizedStrict>
                </StandardMainPanel>

                <LocalizedStrict id="extra-services-create-settings-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="extra-services-create-active-switch">
                            <FormikSwitch<CreateUpdateExtraServiceFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="extra-services-create-meeting-time-required-switch">
                            <FormikSwitch<CreateUpdateExtraServiceFormValues> name="isMeetingTimeRequired">
                                Require meeting time
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="extra-services-create-meeting-location-required-switch">
                            <FormikSwitch<CreateUpdateExtraServiceFormValues> name="isMeetingLocationRequired">
                                Require meeting location
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="extra-services-create-when-add-to-order-field" attrs={{ label: true }}>
                            <FormikDropdown
                                name="action"
                                endAdornment={
                                    action ? (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setFieldValue("action", undefined)
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faClose} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }
                                label="When add to order"
                            >
                                {[...iterateStringEnum(ExtraServiceActionType)].map((action) => (
                                    <MenuItem key={action} value={action}>
                                        <LocalizedEnum
                                            base="extra-services-create-form-action-dropdown-item"
                                            enumValue={action}
                                        >
                                            <span>{action}</span>
                                        </LocalizedEnum>
                                    </MenuItem>
                                ))}
                            </FormikDropdown>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-barcode-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Product number" variant="collapsible" expanded={true}>
                        <SelectProductGroupCode />
                    </StandardSidePanel>
                </LocalizedStrict>

                <LocalizedStrict id="extra-services-create-service-time-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Service time" variant="collapsible">
                        <Grid container gap={3} flexDirection="column">
                            <Grid container item gap={1}>
                                {Object.values(DaysOfWeek).map((day) => {
                                    return (
                                        <Grid key={day} item>
                                            <LocalizedEnum base="merchant-menu-planning-week-day" enumValue={day}>
                                                <FormikChip id={day} name="serviceTime.weekDays" value={day}>
                                                    {day.toLowerCase()}
                                                </FormikChip>
                                            </LocalizedEnum>
                                        </Grid>
                                    )
                                })}
                                <ErrorMessage name="serviceTime">
                                    {(msg) =>
                                        typeof msg === "string" ? <FormHelperText error>{msg}</FormHelperText> : null
                                    }
                                </ErrorMessage>
                            </Grid>
                            <Grid container item gap={2} sm={12} direction="row">
                                <Grid item sm>
                                    <LocalizedStrict id="product-group-form-start-time-picker" attrs={{ label: true }}>
                                        <FormikTimePicker label={"start time"} name="serviceTime.timeInterval.start" />
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item sm>
                                    <LocalizedStrict id="product-group-form-end-time-picker" attrs={{ label: true }}>
                                        <FormikTimePicker label="end time" name="serviceTime.timeInterval.end" />
                                    </LocalizedStrict>
                                </Grid>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </>
    )
}
//
